// Floating shortcut to the homepage checklist, shown on every page. On the homepage it appears once the checklist has
// scrolled out of view and scrolls back to it; on other pages it is always there and links to it.
import { useEffect, useState } from 'react'
import { nowSteps } from './data'
import { useT } from './i18n'
import { Ico, MONO, Sym } from './ui'

const TODO_KEY = 'invoicenow-hub-todo'
const SESSION_KEY = 'invoicenow-hub-pill-closed'

export const loadDone = () => { try { return Math.min(nowSteps.length, Math.max(0, parseInt(localStorage.getItem(TODO_KEY) ?? '0', 10) || 0)) } catch { return 0 } }
export const saveDone = (n: number) => { try { localStorage.setItem(TODO_KEY, String(n)) } catch { /* private mode */ } }

/** `done` given: homepage, progress owned by the checklist. Omitted: another page, progress read from storage. */
export function ChecklistPill({ done: doneProp }: { done?: number }) {
  const { t, L } = useT()
  const onHome = doneProp !== undefined
  const [away, setAway] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [stored, setStored] = useState(0)
  const [closed, setClosed] = useState(false)
  // Client only (after hydration): the prerendered HTML has no pill, and storage is not readable on the server.
  useEffect(() => {
    setMounted(true)
    setStored(loadDone())
    try { setClosed(sessionStorage.getItem(SESSION_KEY) === '1') } catch { /* private mode */ }
  }, [])
  const setClosedPersist = (v: boolean) => { setClosed(v); try { sessionStorage.setItem(SESSION_KEY, v ? '1' : '0') } catch { /* private mode */ } }
  // Homepage: show only once the checklist has scrolled out of view (IntersectionObserver: no layout reads on scroll).
  useEffect(() => {
    if (!onHome) return
    const el = document.getElementById('what-to-do')
    if (!el) return
    const io = new IntersectionObserver(([e]) => setAway(!e.isIntersecting && e.boundingClientRect.bottom < 80), { rootMargin: '-80px 0px 0px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [onHome])
  if (!mounted || (onHome && !away)) return null
  const done = onHome ? doneProp : stored
  const total = nowSteps.length
  if (closed) return (
    <button className="todo-tab" onClick={() => setClosedPersist(false)} aria-label={t('checklist.pill.showAria', { done, total })} title={t('checklist.pill.show')}>
      <span className="count">{done}/{total}</span>
      <Ico n="checklist" size={26} color="#052620" />
    </button>
  )
  const inner = <>
    <Ico n="checklist" size={22} color="#052620" />
    <span style={{ whiteSpace: 'nowrap' }}>{t('checklist.pill.back')}</span>
    <span className="sub" style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.06em', opacity: 0.75, whiteSpace: 'nowrap' }}>{t('checklist.pill.done', { done, total })}</span>
    <Sym n={onHome ? 'arrow_upward' : 'arrow_forward'} className={onHome ? 'arrow' : 'arrow fwd'} style={{ fontSize: 20 }} />
  </>
  return (
    <div className="todo-pill" role="region" aria-label={t('checklist.pill.region')}>
      {onHome
        ? <button className="todo-pill-go" onClick={() => document.getElementById('what-to-do')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{inner}</button>
        : <a className="todo-pill-go" href={L('/') + '#what-to-do'}>{inner}</a>}
      <button className="todo-pill-x" onClick={() => setClosedPersist(true)} aria-label={t('checklist.pill.hide')}><Ico n="close" size={20} color="#052620" /></button>
    </div>
  )
}
